<script setup lang="ts">
import UIStarIcon from '@/components/ui/UIStarIcon.vue'
import { useDetectBreakpoint } from '@/composables/useDetectBreakpoint'
import { IPillLinkItem } from '@lifk/types'
import { computed } from 'vue'
import UIReferenceIcon from '../UIReferenceIcon.vue'

export type TPanelCardState = 'active' | 'muted' | 'default'

const props = withDefaults(
  defineProps<{
    header?: string
    label?: string
    state?: TPanelCardState
    disabled?: boolean
    reference?: IPillLinkItem
    showStarBtn?: boolean
    starred?: boolean
    starIsShy?: boolean
    starLabel?: string
    presentational?: boolean
    variant: keyof typeof variants
  }>(),
  {
    state: 'default',
    starIsShy: true
  }
)

const emit = defineEmits<{
  (e: 'cardClicked'): void
  (e: 'starClicked'): void
}>()

const variants = {
  siteCard: {
    container: 'p-3',
    header: 'text-body font-bold pb-[10px]',
    dividerLine: true,
    body: 'text-body-sm font-medium'
  },
  conditionCard: {
    container: 'p-3',
    header: 'text-body font-bold',
    dividerLine: true,
    body: 'text-body-sm'
  },
  forecastCard: {
    container: 'px-3 py-2',
    header: 'text-body font-bold min-w-[112px] shrink-0 sm:pt-[15px]',
    dividerLine: false,
    body: 'text-body-sm items-center'
  },
  tipCard: {
    container: 'p-5',
    header: 'text-heading-sans font-medium pb-1',
    dividerLine: false,
    body: 'text-body'
  },
  resourceCard: {
    container: 'pb-5',
    header: 'text-heading-sans font-medium px-5 pb-1 flex-col !items-start',
    dividerLine: false,
    body: 'text-body px-5'
  },
  simpleCard: {
    container: 'p-3',
    header: 'text-body font-bold min-w-[112px] shrink-0',
    dividerLine: false,
    body: 'text-body-sm items-start sm:items-center'
  }
}

const onClick = () => {
  if (!props.disabled) emit('cardClicked')
}

const onKeyDown = (e) => {
  if (e.key === 'Enter') onClick()
}

const isMobile = computed(() => useDetectBreakpoint('xs'))
</script>

<template>
  <div
    class="bg-elevated rounded group relative"
    :class="[
      variants[variant].container,
      state == 'active'
        ? 'shadow border-active border-medium -m-[1px]'
        : 'border border-slate-tint-5',
      state == 'muted' ? 'opacity-50' : '',
      {
        'cursor-pointer hover:shadow hover:bg-elevated/80 hover:border-slate-tint-3 active:shadow active:border-active active:border-medium active:-m-[1px]':
          !disabled
      }
    ]"
    :tabindex="presentational ? undefined : disabled ? '' : 0"
    :aria-label="presentational ? undefined : (label ?? header)"
    @click="onClick"
    @keydown="onKeyDown"
  >
    <slot name="heroImg"></slot>
    <header
      v-if="header"
      class="flex gap-x-1 items-center min-h-[24px] text-emphasis"
      :class="variants[variant].header"
    >
      <slot name="headerIcon"></slot>
      <!-- props.text is always from i18n -->
      <!-- eslint-disable vue/no-v-html -->
      <p
        class="overflow-x-auto break-words"
        :class="{ 'flex-grow': variant !== 'forecastCard' }"
        v-html="header"
      ></p>
      <UIReferenceIcon
        v-if="reference"
        class="opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-100"
        tooltip-direction="top"
        :tooltip-alignment="variant === 'forecastCard' ? 'right' : 'left'"
        :reference="reference"
        origin="right_panel"
      />
      <slot name="headerControls"></slot>
    </header>

    <div
      :class="[variants[variant].body, !header ? 'flex gap-4 sm:gap-1' : '']"
      class="w-full text-default"
    >
      <slot></slot>
      <div class="flex gap-1 sm:gap-1">
        <div
          v-if="reference && !header"
          class="opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-100"
        >
          <UIReferenceIcon
            tooltip-direction="left"
            tooltip-alignment="bottom"
            :reference="reference"
            origin="right_panel"
          />
        </div>
        <div
          :class="{
            'opacity-100 transition-opacity duration-100': !starIsShy,
            'sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-100':
              starIsShy
          }"
        >
          <UIStarIcon
            v-if="showStarBtn && !header"
            :size="isMobile ? 24 : 14"
            :label="starLabel ?? label ?? header ?? ''"
            tooltip-direction="top"
            tooltip-alignment="left"
            :active="starred"
            @click.stop.prevent="$emit('starClicked')"
          />
        </div>
      </div>
    </div>
  </div>
</template>
